import * as React from 'react';
import {useCallback, useEffect, useMemo} from 'react';
import {Box, Grid} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useFormik} from 'formik';
import * as actions from '../../store/actions';
import {
    addDevicePhoneBookValidationSchema,
    RenameDevicePhoneBookDialogProps,
    RenamePhoneDirectoryForm,
    useStyles,
} from './RenameDevicePhoneBookDialog.utils';
import {ReduxState} from '../../store/types';
import PermissionProvider from '../../components/PermissionProvider/PermissionProvider';
import {Permission} from '../../store/types/Permission';
import DialogContainer, {DialogButton} from '../../components/AlertDialog/DialogContainer';
import Loader from '../../components/Loader/Loader';
import CustomizedTextField from '../../components/TextField/TextField';
import { showToast } from '../../utils/showToast';

const RenameDevicePhoneBookDialog: React.FC<RenameDevicePhoneBookDialogProps> = ({
        directory,
        isOpen,
        clickOnClose,
    }) => {
    const {t} = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();

    const {
        isUpdatingPhoneBook, updateApiErrorPhoneBookDetails
    } = useSelector((state: ReduxState) => state.devices);

    const isLoading: boolean = useMemo(
        () => isUpdatingPhoneBook || false,
        [isUpdatingPhoneBook],
    );

    const initialValues = useMemo(() => {
        return {
            name: directory?.name ?? ''
        } as RenamePhoneDirectoryForm;
    }, [directory]);

    const submitFunc = useCallback((form: RenamePhoneDirectoryForm) => {
        if(!directory) {
            return;
        }

        const model = {
            ...directory,
            name: form.name, 
            callback: () => {
                showToast(t<string>('screens:devicePhoneBooks.devicePhoneBookRenamed'));
                clickOnClose?.(true);
            }
        };

        dispatch(actions.updatePhoneBookDetails.request(model));

    }, [directory]);

    const {
        values,
        handleSubmit,
        resetForm,
        errors,
        setFieldError,
        handleChange,
        dirty
    } = useFormik<RenamePhoneDirectoryForm>({
        initialValues,
        onSubmit: submitFunc,
        validationSchema: addDevicePhoneBookValidationSchema,
        enableReinitialize: true,
        validateOnChange: false,
        validateOnBlur: true
    });

    useEffect(() => {
        if(updateApiErrorPhoneBookDetails?.faultstring?.length && !errors?.name?.length)
        {
            setFieldError('name', updateApiErrorPhoneBookDetails.faultstring);
        }
    }, [updateApiErrorPhoneBookDetails, errors, setFieldError])

    useEffect(() => {
        if (!isOpen) {
            resetForm();
        }
    }, [isOpen]);

    const isDisabled = (): boolean => {
        return (!dirty ||
            !(values.name?.length) ||
            isLoading);
    }

    return (
        <PermissionProvider permission={Permission.Inventory.DeviceProfiles.value}>
            <DialogContainer
                className={classes.modalContainer}
                isOpen={isOpen}
                dataQa={'device-phone-book-rename-modal'}
                header={t('screens:devicePhoneBooks.renamePhoneBookDialogTitle')}
                headerClass={classes.header}
                dialogActionsButtons={[
                    <DialogButton
                        key="cancel"
                        label={t('common:cancel')}
                        onClick={() => {
                            clickOnClose?.(false);
                        }}
                    />,
                    <DialogButton
                        key="save"
                        label={t('common:save')}
                        disabled={
                            isDisabled()
                        }
                        onClick={handleSubmit}
                        dataQa={'rename-new-device-phone-book-save-button'}
                        primary
                    />,
                ]}
            >
                <form
                    className={classes.form}
                    autoComplete="off"
                    data-testid="rename-new-device-phone-book-form"
                >
                    <Grid className={classes.itemsContainer}>
                        <Box className={classes.boxRow}>
                            <CustomizedTextField
                                id="name"
                                label={t('screens:devicePhoneBooks.name')}
                                value={values.name}
                                helperText={
                                    errors.name && errors.name.length > 0
                                        ? errors.name
                                        : ''
                                }
                                setFieldError={setFieldError}
                                onChange={handleChange}
                                required
                                maxLength={64}
                                dataQa="device-phone-book-rename-field"
                            />
                        </Box>
                    </Grid>
                </form>
                {isOpen && isLoading && (
                    <Loader dataQa={'wait-for-data'} absolutePosition/>
                )}
            </DialogContainer>
        </PermissionProvider>
    );
};

export default RenameDevicePhoneBookDialog;
